import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {useMarkerStore} from "../../../../store/markerStore";
import useMapStore from "../../../../store/mapStore";
import useLegendStore from "../../../../store/legendStore";
import {IPolygonLayer} from "../../../../types/map";
import {ILegend} from "../../../../types/home";
import {IFeature} from "../../../../api/endpoints/FileEndPoint";
import {Position} from "deck.gl";
import {ConvertHexToRGBA} from "../../../../utils/common";
import {IMarker} from "../../../../types/marker";
import {useSurfaceMapMutation} from "../../../../queries/useSurfaceMapMutation";

export const useSurfaceMap = (
  result: any,
  setResult: (result: any) => void,
  legendStartEndValues: [number | null, number | null],
  point: IMarker | null
) => {
  const [showSurfaceMapSection, setShowSurfaceMapSection] = useState(true)
  const prevShowSurfaceMapRef = useRef(false)

  const updateMarker = useMarkerStore(state => state.updateMarker)

  const mapBoundsZoom = useMapStore(state => state.mapBoundsZoom)

  const showSurfaceMap = useLegendStore(state => state.showSurfaceMap)
  const setShowSurfaceMap = useLegendStore(state => state.setShowSurfaceMap)
  const legend = useLegendStore(state => state.legend)
  const setLegend = useLegendStore(state => state.setLegend)
  const getLayerSurfaceMaps = useSurfaceMapMutation()

  const { bounds, zoom, index } = useMemo(() => {
    const getApiIndex = (zoomLevel: number) => {
      let i = 5
      switch (true) {
        case zoomLevel < 5:
          i = 1
          break
        case zoomLevel >= 5 && zoomLevel < 7:
          i = 2
          break
        case zoomLevel >= 7 && zoomLevel < 9:
          i = 3
          break
        case zoomLevel >= 9 && zoomLevel < 11:
          i = 4
          break
      }
      return i
    }
    return {
      bounds: mapBoundsZoom.bounds,
      zoom: mapBoundsZoom.zoom,
      index: getApiIndex(mapBoundsZoom.zoom)
    }
  }, [mapBoundsZoom])

  const updateEnableSurfaceMap = useCallback(async (state: boolean) => {
    setShowSurfaceMap(state)

    if (!state && point) {
      updateMarker(point.id, {
        surfaceMaps: {
          selectedLevel1: point.surfaceMaps.selectedLevel1,
          selectedLevel2: '',
          overlays: point.surfaceMaps.overlays,
          categories: point.surfaceMaps.categories
        }
      })

      setLegend({ ...legend, display: state })
    }
  }, [point, legend, setLegend, setShowSurfaceMap, updateMarker])

  const handleSurfaceMap = useCallback((levels: string, fromEffect: boolean) => {
    if (levels === 'none') {
      updateEnableSurfaceMap(false)
      return
    }
    if (!fromEffect && !showSurfaceMap) {
      updateEnableSurfaceMap(true)
    }
    const selectedLevels = levels.split('|')
    if (selectedLevels.length > 1 && point && point.surfaceMaps) {
      updateMarker(
        point.id,
        {
          surfaceMaps: {
            selectedLevel1: selectedLevels[0],
            selectedLevel2: selectedLevels[1],
            overlays: point.surfaceMaps.overlays,
            categories: point.surfaceMaps.categories
          }
        }
      )
    }
  }, [point, showSurfaceMap, updateEnableSurfaceMap, updateMarker])

  const updateSurfaceMap = useCallback(async (
    level1: string,
    level2: string,
    bounds: number[],
    apiIndex: number
  ) => {
    if (!point) return

    const surfaceMap = point.surfaceMaps.overlays.find(
      (x) => x.Level1 === level1 && x.Level2 === level2
    )

    if (surfaceMap) {
      try {
        const payload = {
          index: apiIndex,
          mapBound: bounds,
          datasetId: point.dataset,
          overlay: surfaceMap
        }
        getLayerSurfaceMaps.mutate(payload, {
          onSuccess: async (result) => {
            setResult(result)
          }
        })
      } catch (error) {
        console.error('Error fetching surface map:', error)
      }
    }else {
      setLegend(null)
    }
  }, [point, setResult])

  useEffect(() => {
    if (!point) return

    const overlay = point.surfaceMaps.overlays.find(
      (x) => x.Level1 === point.surfaceMaps.selectedLevel1 &&
        x.Level2 === point.surfaceMaps.selectedLevel2
    )
    if (overlay && result) {
      const data = new Array<IPolygonLayer>()
      const newLegend = {
        title: 'Legend',
        name: overlay.Level1,
        unit: overlay.Unit,
        display: true,
        values: [],
      } as ILegend

      if (result.featureCollection && result.featureCollection.features && result.featureCollection.features.length > 0) {

        const minValue = legendStartEndValues[0] === null ? result.minDepth : legendStartEndValues[0]
        const maxValue = legendStartEndValues[1] === null ? result.maxDepth : legendStartEndValues[1]
        const range = (maxValue - minValue) / overlay.LegendColor.length

        for (let i = 1; i <= overlay.LegendColor.length; i++) {
          newLegend.values.push({
            color: overlay.LegendColor[i - 1],
            value: minValue + range * i,
          })
        }

        (result.featureCollection.features as IFeature[]).forEach((feature) => {
          const index = []
          for (const x in feature.properties) {
            index.push(x)
          }

          const propertyValue = feature.properties[index[0]]
          let legendItem = newLegend.values.find(
            (x) => x.value >= propertyValue
          )

          if (legendItem === undefined) {
            legendItem = newLegend.values[newLegend.values.length - 1]
          }

          const color = legendItem !== undefined ? legendItem.color : '';

          (feature.geometry.coordinates as Position[][]).forEach((polygon: Position[]) => {
            data.push({
              id: crypto.randomUUID(),
              coordinates: polygon,
              lineColor: ConvertHexToRGBA(color, 0),
              color: ConvertHexToRGBA(color),
              value: propertyValue.toFixed(2).toString(),
            })
          })
        })
      }

      const updatedOverlay = {
        ...overlay,
        data: data,
      }

      const updatedOverlays = [...point.surfaceMaps.overlays]
      const overlayIndex = updatedOverlays.findIndex(
        (x) => x.Level1 === overlay.Level1 && x.Level2 === overlay.Level2
      )

      if (overlayIndex !== -1) {
        updatedOverlays[overlayIndex] = updatedOverlay
      } else {
        updatedOverlays.push(updatedOverlay)
      }

      updateMarker(point.id, {
        surfaceMaps: {
          ...point.surfaceMaps,
          overlays: updatedOverlays,
        },
      })

      const hasData = updatedOverlay && updatedOverlay.data && updatedOverlay.data.length > 0
      const title = newLegend.name

      setLegend({
        ...newLegend,
        display: hasData,
        title: title,
        name: overlay.Level2,
      })
    }
  }, [result, legendStartEndValues])

  useEffect(() => {
    const prevShowSurfaceMap = prevShowSurfaceMapRef.current
    prevShowSurfaceMapRef.current = showSurfaceMap

    if (
      showSurfaceMap &&
      prevShowSurfaceMap &&
      point &&
      point.surfaceMaps &&
      !point.surfaceMaps.selectedLevel2
    ) {
      if (point.surfaceMaps.categories.length > 0) {
        const firstCategory = point.surfaceMaps.categories[0]
        const firstOverlay = point.surfaceMaps.overlays.find((o) => o.Level1 === firstCategory.name)
        handleSurfaceMap(firstOverlay !== undefined ? `${firstOverlay.Level1}|${firstOverlay.Level2}`: '' , true)
      } else {
        setLegend(null)
      }
    }
  }, [point?.surfaceMaps, showSurfaceMap])

  useEffect(() => {
    if (bounds[0] === 0 && bounds[1] === 0 && bounds[2] === 0 && bounds[3] === 0 && zoom === 1) {
      return
    }
    if (
      point &&
      point.surfaceMaps &&
      point.surfaceMaps.selectedLevel1 &&
      point.surfaceMaps.selectedLevel2
    ) {
      updateSurfaceMap(
        point.surfaceMaps.selectedLevel1,
        point.surfaceMaps.selectedLevel2,
        bounds,
        index
      )
    }
  }, [
    bounds,
    zoom,
    index,
    point &&
    point.surfaceMaps &&
    point.surfaceMaps.selectedLevel1 &&
    point.surfaceMaps.selectedLevel2
  ])

  return {
    showSurfaceMapSection,
    setShowSurfaceMapSection,
    showSurfaceMap,
    handleSurfaceMap
  }
}
